/**
 * PWA service worker registration.
 *
 * GUARDRAILS FOR FUTURE AGENTS:
 * - Do NOT delete or rename this file.
 * - The SW is only registered in production builds. `npm start` intentionally
 *   skips registration so caching never interferes with hot-reload in dev.
 * - Call register() in index.js to enable offline + installable PWA.
 */

const isLocalhost = Boolean(
  window.location.hostname === 'localhost' ||
    window.location.hostname === '[::1]' ||
    /^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/.test(
      window.location.hostname
    )
);

/**
 * Register the service worker. Accepts an optional config object:
 *   config.onSuccess(registration) — called when SW caches content for the first time
 *   config.onUpdate(registration)  — called when a new SW is waiting to activate
 */
export function register(config) {
  if (process.env.NODE_ENV === 'production' && 'serviceWorker' in navigator) {
    const publicUrl = new URL(process.env.PUBLIC_URL, window.location.href);
    // Don't register if the page is served from a different origin than PUBLIC_URL.
    if (publicUrl.origin !== window.location.origin) return;

    window.addEventListener('load', () => {
      const swUrl = `${process.env.PUBLIC_URL}/service-worker.js`;
      if (isLocalhost) {
        // On localhost, confirm the SW exists before registering.
        checkValidServiceWorker(swUrl, config);
        navigator.serviceWorker.ready.then(() => {
          console.log('[PWA] Serving cache-first via service worker (localhost).');
        });
      } else {
        registerValidSW(swUrl, config);
      }
    });
  }
}

function registerValidSW(swUrl, config) {
  navigator.serviceWorker
    .register(swUrl)
    .then((registration) => {
      registration.onupdatefound = () => {
        const installing = registration.installing;
        if (!installing) return;
        installing.onstatechange = () => {
          if (installing.state === 'installed') {
            if (navigator.serviceWorker.controller) {
              // A new version of the app is ready.
              console.log('[PWA] New content available — reload to update.');
              config?.onUpdate?.(registration);
            } else {
              // First-time install — app is now cached for offline use.
              console.log('[PWA] App cached for offline use.');
              config?.onSuccess?.(registration);
            }
          }
        };
      };
    })
    .catch((err) => {
      console.error('[PWA] Service worker registration failed:', err);
    });
}

function checkValidServiceWorker(swUrl, config) {
  fetch(swUrl, { headers: { 'Service-Worker': 'script' } })
    .then((response) => {
      const contentType = response.headers.get('content-type');
      if (
        response.status === 404 ||
        (contentType && !contentType.includes('javascript'))
      ) {
        // No service worker found — reload to remove any stale SW.
        navigator.serviceWorker.ready.then((reg) =>
          reg.unregister().then(() => window.location.reload())
        );
      } else {
        registerValidSW(swUrl, config);
      }
    })
    .catch(() => {
      console.log('[PWA] No internet — app running in offline mode.');
    });
}
